import gql from 'graphql-tag';
import { useQuery } from 'urql';

import { GameDescriptor } from '@/fragments/GameDescriptor';
import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  PlayerTransactionsQuery,
  PlayerTransactionsQueryVariables,
} from './__generated__/usePlayerTransactions';
import { queryParams } from './queryParams';

const PLAYER_TRANSACTION_QUERY = gql`
  query PlayerTransactions(
    $adjustmentCategory: AdjustmentCategory
    $adjustmentType: AdjustmentType
    $after: String
    $before: String
    $desc: Boolean
    $first: Int
    $from: OffsetDateTime
    $gameRoundId: String
    $id: String
    $last: Int
    $maxAmount: PositiveBigDecimal
    $minAmount: PositiveBigDecimal
    $orderBy: TransactionSearchOrder
    $paymentMethod: String
    $paymentStatus: [PaymentStatus!]
    $playerId: ID!
    $rawId: String
    $to: OffsetDateTime
    $transactionTypes: [TransactionType!]
  ) {
    player(playerId: $playerId) {
      id
      transactionsV2(
        adjustmentCategory: $adjustmentCategory
        adjustmentType: $adjustmentType
        after: $after
        before: $before
        desc: $desc
        first: $first
        from: $from
        gameRoundId: $gameRoundId
        id: $id
        last: $last
        maxAmount: $maxAmount
        minAmount: $minAmount
        orderBy: $orderBy
        paymentMethod: $paymentMethod
        paymentStatus: $paymentStatus
        rawId: $rawId
        to: $to
        transactionTypes: $transactionTypes
      ) {
        edges {
          node {
            rawTransactionId
            uuid
            transactionId
            playerId
            gameId
            gameRoundId
            isRoundFinished
            extTransactionId
            payload
            gameSessionId
            provider
            gameProvider
            brand {
              id
              name
            }
            processedAt
            paymentMethod
            paymentStatus
            transactionType
            openingBalance
            closingBalance
            currency
            comment
            amount
            initiator {
              ... on PlayerInitiator {
                player {
                  id
                  firstName
                  lastName
                  email
                }
              }
              ... on AgentInitiator {
                agent {
                  agentId
                  email
                  firstName
                  lastName
                }
              }
            }
            gameDescriptor: gameDescriptorV2 {
              ...GameDescriptor
            }
            bonusTransactionData {
              bonusId
              amount
              openingBalance
              closingBalance
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
  ${GameDescriptor}
`;

export default function usePlayerTransactions(playerId: string) {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const [{ data, fetching }, refresh] = useQuery<
    PlayerTransactionsQuery,
    PlayerTransactionsQueryVariables
  >({
    query: PLAYER_TRANSACTION_QUERY,
    variables: {
      playerId,
      ...mapVariables(query),
    },
  });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => {
      setQuery(values, 'replaceIn');
    },
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, data?.player.transactionsV2.pageInfo),
    transactions: data?.player.transactionsV2?.edges?.map((edge) => edge?.node),
  };
}
